<template>
    <div class="mx-2 mx-lg-5">
        <div class="card mt-3">
            <div class="card-header">
                <h3>{{ $t("headers.patrolPhotos") }}</h3>
            </div>
            <div class="card-body">
                <div class="row mb-5 align-items-end">
                    <div class="col-12 col-md-4">
                        <label class="form-label fw-bold">
                            {{ $t("patrolPhoto.device") }}
                        </label>
                        <select v-model="selectedDevice" class="form-select">
                            <option value="">{{ $t("patrolPhoto.allDevices") }}</option>
                            <option v-for="device in devices" :key="device.id" :value="device.id">
                                {{ device.device_name }}
                            </option>
                        </select>
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label fw-bold">
                            {{ $t("patrolPhoto.from") }}
                        </label>
                        <input type="date" v-model="dateFrom" class="form-control" />
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label fw-bold">
                            {{ $t("patrolPhoto.to") }}
                        </label>
                        <input type="date" v-model="dateTo" class="form-control" />
                    </div>
                    <div class="col-12 col-md-2 d-flex">
                        <button class="btn btn-secondary w-100" @click="resetFilters">
                            {{ $t("utils.clearFilters") }}
                        </button>
                    </div>
                </div>

                <div v-if="!photos.length" class="text-center text-muted py-4 fs-5">
                    {{ $t("patrolPhoto.noPhotos") }}
                </div>
                <div v-else class="d-flex flex-wrap gap-3">
                    <div
                        v-for="(photo, index) in photos"
                        :key="photo.id"
                        class="photo-thumb"
                        @click="openLightbox(index)"
                    >
                        <img :src="getPhotoUrl(photo.id, true)" class="thumb-img" />
                        <div class="thumb-meta">
                            <small class="text-muted d-block">{{ photo.device_name }}</small>
                            <small class="text-muted">{{ photo.created_at ? $d(new Date(photo.created_at), 'short') : '—' }}</small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" ref="lightboxModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content modal_wrapper">
                <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                    <div>
                        <h3 class="modal-title mb-0">{{ $t("patrolPhoto.photo") }}</h3>
                        <small v-if="currentPhoto" class="text-muted">
                            {{ currentPhoto.device_name }} &bull;
                            {{ currentPhoto.created_at ? $d(new Date(currentPhoto.created_at), 'short') : '—' }}
                        </small>
                        <div v-if="currentPhoto && currentPhoto.note" class="mt-1">
                            {{ currentPhoto.note }}
                        </div>
                    </div>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-auto position-relative w-100 text-center">
                    <button
                        class="btn btn-primary position-absolute top-50 start-0 translate-middle-y px-3 py-4"
                        @click="prevPhoto"
                    >
                        <icon icon="PlayerPrev"></icon>
                    </button>
                    <img v-if="currentPhoto" :src="getPhotoUrl(currentPhoto.id)" class="img-fluid lightbox-img" />
                    <button
                        class="btn btn-primary position-absolute top-50 end-0 translate-middle-y px-3 py-4"
                        @click="nextPhoto"
                    >
                        <icon icon="PlayerNext"></icon>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import { CONFIG } from "@/config.js";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";

export default {
    components: {
        Icon,
    },
    data() {
        return {
            photos: [],
            devices: [],
            selectedDevice: "",
            dateFrom: "",
            dateTo: "",
            currentIndex: 0,
            lightboxInstance: null,
        }
    },
    computed: {
        currentPhoto() {
            return this.photos[this.currentIndex] || null;
        }
    },
    created() {
        this.getDevices();
        this.getPhotos();
    },
    watch: {
        selectedDevice() { this.getPhotos(); },
        dateFrom() { this.getPhotos(); },
        dateTo() { this.getPhotos(); },
    },
    methods: {
        getPhotos() {
            const params = {};
            if (this.selectedDevice) params.device = this.selectedDevice;
            if (this.dateFrom) params.date_from = this.dateFrom;
            if (this.dateTo) params.date_to = this.dateTo;

            this.$store.getters.api.get(`/dashboard/${this.$route.params.slug}/patrol-photos`, { params })
                .then(res => {
                    this.photos = res.data;
                })
                .catch(error => {
                    console.error("Error loading patrol photos.", error);
                });
        },
        resetFilters() {
            this.selectedDevice = "";
            this.dateFrom = "";
            this.dateTo = "";
        },
        getDevices() {
            this.$store.getters.api.get(`/dashboard/${this.$route.params.slug}/mobile-devices`)
                .then(res => {
                    this.devices = res.data;
                })
                .catch(error => {
                    console.error("Error loading devices.", error);
                });
        },
        getPhotoUrl(photoId, thumb = false) {
            let url = `${CONFIG.api.baseUrl}/dashboard/${this.$route.params.slug}/patrol-photos/${photoId}`;
            if (thumb) url += "?thumb=true";
            return url;
        },
        openLightbox(index) {
            this.currentIndex = index;
            if (!this.lightboxInstance) {
                this.lightboxInstance = new Modal(this.$refs.lightboxModal);
            }
            this.lightboxInstance.show();
        },
        nextPhoto() {
            this.currentIndex = (this.currentIndex + 1) % this.photos.length;
        },
        prevPhoto() {
            this.currentIndex = (this.currentIndex - 1 + this.photos.length) % this.photos.length;
        },
    }
}
</script>

<style scoped>
.photo-thumb {
    cursor: pointer;
    width: 180px;
}

.thumb-img {
    width: 180px;
    height: 135px;
    object-fit: cover;
    border-radius: 4px;
    transition: opacity 0.15s;
}

.thumb-img:hover {
    opacity: 0.85;
}

.thumb-meta {
    padding: 4px 2px 0;
}

.lightbox-img {
    max-height: 75vh;
}
</style>
